import * as React from 'react';

import type {
    GetInvoiceById_invoice as Invoice,
    SalesDocumentUpdateInput,
} from 'nutshell-graphql-types';

import {BulletedList} from '../bulleted-list/bulleted-list';
import {Checkbox} from '../checkbox/checkbox';
import {colors} from '../colors/colors';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {LoadingText} from '../loading-placeholder/loading-text';
import {SaveableWrapper} from '../saveable-wrapper/saveable-wrapper';
import {Body} from '../typography/body';

import {QUOTE_DETAILS} from './helpers';

type Props = {
    quote: Invoice;
    isActive: boolean;
    onCancel: () => void;
    onSave: (quote: SalesDocumentUpdateInput) => void;
    onToggleActive: (sectionName: string) => void;
    forceSave: boolean;
};

export function InvoiceTemplateDetails(props: Props) {
    const [purchaseOrderNumber, setPurchaseOrderNumber] = React.useState<boolean>(
        props.quote && props.quote.purchaseOrderNumber !== null
    );
    const [jobNumber, setJobNumber] = React.useState<boolean>(
        props.quote && props.quote.jobNumber !== null
    );
    const [customerNumber, setCustomerNumber] = React.useState<boolean>(
        props.quote && props.quote.customerNumber !== null
    );
    const [referenceNumber, setReferenceNumber] = React.useState<boolean>(
        props.quote && props.quote.referenceNumber !== null
    );

    const loadingTextElements = React.useMemo(() => {
        return [...Array(4)].map((_, index) => (
            <LoadingText
                key={index.toString()}
                minWidth={150}
                maxWidth={300}
                height={20}
                color={colors.offWhiteDk}
                isAnimated={false}
            />
        ));
    }, []);

    const enabledFields = React.useMemo(() => {
        const fields = [];
        if (purchaseOrderNumber) fields.push('Purchase order #');
        if (jobNumber) fields.push('Job #');
        if (customerNumber) fields.push('Customer #');
        if (referenceNumber) fields.push('Reference #');

        return fields;
    }, [purchaseOrderNumber, jobNumber, customerNumber, referenceNumber]);

    const handleSave = React.useCallback(() => {
        props.onSave({
            salesDocument: props.quote.id,
            purchaseOrderNumber: purchaseOrderNumber ? '' : null,
            jobNumber: jobNumber ? '' : null,
            customerNumber: customerNumber ? '' : null,
            referenceNumber: referenceNumber ? '' : null,
        });
    }, [purchaseOrderNumber, jobNumber, customerNumber, referenceNumber, props]);

    React.useEffect(() => {
        if (props.forceSave) {
            handleSave();
        }
    }, [props.forceSave, handleSave]);

    const handleCancel = () => {
        setPurchaseOrderNumber(props.quote && props.quote.purchaseOrderNumber !== null);
        setJobNumber(props.quote && props.quote.jobNumber !== null);
        setCustomerNumber(props.quote && props.quote.customerNumber !== null);
        setReferenceNumber(props.quote && props.quote.referenceNumber !== null);
        props.onCancel();
    };

    return (
        <SaveableWrapper
            isActive={props.isActive}
            onCancel={handleCancel}
            onSave={handleSave}
            onToggleActive={props.onToggleActive}
            sectionName={QUOTE_DETAILS}
            overflowVisible={false}
            noPadding={true}
            showEditIcon={true}
        >
            <div className='flex pad-16 full-width background-offwhite'>
                <StackLegacy spacing={16}>
                    <StackLegacy spacing={8} isFullWidth={true}>
                        {loadingTextElements}
                    </StackLegacy>
                    {props.isActive && (
                        <StackLegacy spacing={20}>
                            <Body>Invoice will include:</Body>
                            <Checkbox
                                label='Purchase order #'
                                checked={purchaseOrderNumber}
                                isLarge={true}
                                onChange={() => setPurchaseOrderNumber(!purchaseOrderNumber)}
                            />
                            <Checkbox
                                label='Job #'
                                checked={jobNumber}
                                isLarge={true}
                                onChange={() => setJobNumber(!jobNumber)}
                            />
                            <Checkbox
                                label='Customer #'
                                checked={customerNumber}
                                isLarge={true}
                                onChange={() => setCustomerNumber(!customerNumber)}
                            />
                            <Checkbox
                                label='Reference #'
                                checked={referenceNumber}
                                isLarge={true}
                                onChange={() => setReferenceNumber(!referenceNumber)}
                            />
                        </StackLegacy>
                    )}
                    {enabledFields.length > 0 && !props.isActive && (
                        <StackLegacy spacing={16}>
                            <Body>Invoice will include:</Body>
                            <BulletedList
                                bullets={enabledFields}
                                size='normal'
                                greyBullets={true}
                                marginBottom={0}
                            />
                        </StackLegacy>
                    )}
                </StackLegacy>
            </div>
        </SaveableWrapper>
    );
}
